/**
 * Subscription tab — the customer's plans (base + add-ons) in one table, printed the way Zoom's own
 * "Current Plans" table prints them (API repo docs/ZOOM-DISPLAY-PARITY.md, T2–T7): Plan Name (the
 * product alone — no term, no capacity), Quantity in Zoom's units, Details, Billing Period, the
 * service-effective / next-invoice dates, and status. The cell rules live in lib/zoomLine.ts. Sync-now
 * (plans & usage) lives here. account_type is a DECOY and is never shown as the plan.
 */

import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import {
  EmptyState,
  ErrorState,
  SkeletonRows,
  StatusPill,
  Table,
  type Column,
} from "@/components/ui";
import { formatDate, formatInt } from "@/lib/format";
import {
  lineBillingPeriod,
  lineDetails,
  lineName,
  productName,
  quantityUnit,
  repeatsProductAbove,
} from "@/lib/zoomLine";
import type { AddonRow } from "@/api/types";
import { FeedNote, Section, statusLabel, statusTone, TierBadge } from "../display";
import { SyncNowControl } from "../SyncNowControl";
import { useCustomerSubscription, useRefreshAfterSync } from "../useCustomer";

export function SubscriptionTab({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  const sub = useCustomerSubscription(accountId, true);
  const { refreshPlansUsage } = useRefreshAfterSync(accountId);

  if (sub.isPending) return <SkeletonRows rows={5} />;
  if (sub.isError) return <ErrorState error={sub.error} onRetry={() => void sub.refetch()} />;

  const { addons, note } = sub.data;

  // BUNDLED-AWARENESS: hide add-on rows whose service is folded into the account tier (bundled ===
  // 'bundled') — they are not a purchased subscription line. FAIL-SAFE: keep 'paid'/'unknown'/null/
  // undefined (an unknown tier is never asserted bundled) and always keep the base row. New array — props
  // are not mutated.
  const visibleAddons = addons.filter((r) => r.bundled !== "bundled");

  const columns: Column<AddonRow>[] = [
    {
      key: "plan",
      header: t("customer.subscription.planName"),
      // T3 · the product alone ("Zoom Workplace Pro" — the term is in Billing Period, the capacity in
      // Details). T2 · a phone calling / add-on line is a "--" child. T7 · a product repeated on the row
      // directly above prints its name once: this row's cell is left blank (BCN Ratchaburi).
      cell: (r, i, shown) =>
        repeatsProductAbove(r, shown[i - 1]) ? (
          ""
        ) : (
          <span className="font-medium text-(--text)">{lineName(r)}</span>
        ),
      sortValue: (r) => productName(r.plan_type),
    },
    {
      key: "qty",
      header: t("customer.subscription.quantity"),
      // T4 · Zoom's units: license(s); Zoom Rooms "1 room" / "3 rooms"; Conference Room Connector ports.
      // W4-Q7c · the counted form "1 license" / "124 licenses" on phone child lines and on the
      // top-level products the corpus prints it for (lib/zoomLine.ts quantityUnit).
      // W4-Q7b · the number carries Zoom's thousands separator ("18,000 license(s)", KMUTT) — the
      // same formatInt the Usage tab's Assigned / Capacity cells use.
      cell: (r) => {
        if (r.hosts == null) return "—";
        const qty = formatInt(r.hosts);
        const one = r.hosts === 1;
        switch (quantityUnit(r)) {
          case "room":
            return t(one ? "customer.subscription.roomOne" : "customer.subscription.roomMany", {
              qty,
            });
          case "port":
            return t(one ? "customer.subscription.portOne" : "customer.subscription.portMany", {
              qty,
            });
          case "license":
            return t(
              one ? "customer.subscription.licenseOne" : "customer.subscription.licenseMany",
              { qty },
            );
          case "license(s)":
            return t("customer.subscription.licenses", { qty });
        }
      },
      sortValue: (r) => r.hosts,
      align: "right",
      numeric: true,
    },
    {
      key: "details",
      header: t("customer.subscription.details"),
      // A3/A4 · only what the corpus shows ("Bundled features", "500 participants", "Pay As You Go
      // Plan", a legacy bundle's components one per line — T6). Zoom's own English in both locales,
      // like the plan names beside it. Nothing proven → "—".
      cell: (r) => {
        const lines = lineDetails(r);
        if (!lines) return "—";
        return (
          <span className="flex flex-col">
            {lines.map((ln) => (
              <span key={ln}>{ln}</span>
            ))}
          </span>
        );
      },
    },
    {
      key: "period",
      header: t("customer.subscription.billingPeriod"),
      // T5 · a fixed-end line (every trial) prints Zoom's "--"; the Pay-As-You-Go shell "Monthly".
      cell: (r) => lineBillingPeriod(r),
    },
    {
      key: "effective",
      header: t("customer.subscription.effective"),
      // ZOOM'S OWN FORM for a fixed-end line (every free trial): "Sep 16, 2026 (expires on Dec 16,
      // 2026)" in THIS cell, and the Next Invoice cell left empty. A trial's end is not a billing
      // date — printing it under "Next Invoice Date" told staff an invoice was coming when nothing
      // will ever be billed.
      cell: (r) =>
        r.is_fixed_end && r.expires_on
          ? `${formatDate(r.service_effective_date)} ${t("customer.subscription.expiresOn", {
              date: formatDate(r.expires_on),
            })}`
          : formatDate(r.service_effective_date),
      sortValue: (r) => r.service_effective_date,
    },
    {
      key: "next",
      header: t("customer.subscription.nextInvoice"),
      // T5 · Zoom leaves this cell EMPTY on a fixed-end line — not "--" (Siriraj's Zoom Events and
      // phone lines, ITOCHU's Global Select Calling Plan, Toyota Tsusho's Translated Captions trial,
      // Round 4's Free Trial lines; owner go 2026-09-19, wave 8). Every other line keeps formatDate()'s
      // "—" for a missing date: the corpus shows no recurring line with an empty Next Invoice Date.
      cell: (r) => (r.is_fixed_end ? "" : formatDate(r.next_invoice_date)),
      sortValue: (r) => (r.is_fixed_end ? null : r.next_invoice_date),
    },
    {
      key: "status",
      header: t("customer.status"),
      cell: (r) =>
        r.status ? (
          <StatusPill tone={statusTone(r.status)}>{statusLabel(t, r.status)}</StatusPill>
        ) : (
          "—"
        ),
      sortValue: (r) => r.status,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <Section
        title={t("customer.tabs.subscription")}
        trailing={
          <TierBadge tier={sub.data.tier ?? null} tierKnown={sub.data.tier_known ?? false} />
        }
      >
        {sub.data.is_free ? (
          // Zoom shows a Free account as this card and NO plan table — even when the mirror still
          // holds rows from a plan that ended (the API sends none for such an account).
          <EmptyState
            title={t("customer.subscription.freePlanTitle")}
            body={t("customer.subscription.freePlanBody")}
          />
        ) : (
          <Table
            columns={columns}
            rows={visibleAddons}
            rowKey={(r) => `${r.plan_category ?? "?"}:${r.plan_type ?? "?"}`}
            caption={t("customer.tabs.subscription")}
            // 'bundle' is the base plan of an Active-Host / Named-Host account — pin it like any base row.
            pinFirst={(r) => r.plan_category === "base" || r.plan_category === "bundle"}
            empty={
              <EmptyState
                title={t("customer.subscription.noAddons")}
                body={t("customer.subscription.noAddonsBody")}
              />
            }
          />
        )}
        {note && !sub.data.is_free && <FeedNote>{note}</FeedNote>}
      </Section>

      <Section title={t("syncNow.title")}>
        <SyncNowControl
          label={t("action.syncPlansUsage")}
          fetcher={() => api.syncPlansUsage(accountId)}
          onOk={refreshPlansUsage}
          lastSyncedAt={sub.data.plan_synced_at}
        />
      </Section>
    </div>
  );
}
